/**
 * Matches the inline mention markup we emit from `MentionTextarea`:
 *   `@[Display Name](user_id)`
 *
 * MUST stay in sync with the server-side `MENTION_REGEX` defined in
 * `server/src/db/controllers/notification.controller/notification-helpers.ts`.
 */
export const MENTION_REGEX = /@\[([^\]]+)\]\(([A-Za-z0-9_-]+)\)/g

export type ParsedMention = {
    user_id: string
    display_name: string
}

/**
 * Extracts all `@[name](user_id)` mentions from a message body. Duplicates
 * (same user mentioned multiple times) are collapsed to one entry, keeping
 * the first display name encountered.
 */
export function parseMentionsFromText(
    text: string | null | undefined,
): ParsedMention[] {
    if (!text) return []

    const seen = new Set<string>()
    const mentions: ParsedMention[] = []

    for (const match of text.matchAll(MENTION_REGEX)) {
        const [, display_name, user_id] = match
        if (!user_id || seen.has(user_id)) continue
        seen.add(user_id)
        mentions.push({ user_id, display_name })
    }

    return mentions
}

/**
 * Strips mention markup down to the human-readable display form. Useful
 * for previews / tooltips where chips would be too heavy.
 */
export function stripMentionMarkup(text: string | null | undefined): string {
    if (!text) return ''
    return text.replace(MENTION_REGEX, (_, displayName) => `@${displayName}`)
}
